/**
 * The force simulation that lays the graph out: nodes push each other apart (`repulsion.ts`),
 * links pull their ends to a preferred length (a node's links out of its group share one link's
 * worth of pull, so groups stay compact), nodes of one category gather in their corner,
 * groups hold their nodes together (`groups.ts`). Each step is scaled by `alpha`, the
 * layout's energy, which cools down until the layout rests.
 *
 * **Big graphs** (from {@link WORKER_FROM} nodes): the push between nodes, most of a step's work
 * (about 150 ms at 40 000 nodes), runs in workers (`repulsionWorker.ts`: up to four, each on a
 * share of the nodes), on the places of the step before; the page keeps drawing and answering the pointer meanwhile, and a step is taken
 * each time the pushes come back ({@link SceneLayout.tick} says whether it took one). Pushes
 * one step old are as good: the layout moves little from one step to the next, and slows down.
 * Where there is no worker (tests, an old browser), the push is computed in the step itself.
 */
import { sameCategory } from "../../shared/categories";
import { linkPull, pullTogether, pushApart } from "../groups";
import { applyPushes, placesOf, repel, type Pushes } from "../repulsion";
import type { PushAnswer, PushRequest } from "../repulsionWorker";
import type { Scene } from "./index";
import type { SimLink, SimNode } from "./types";
import { CENTRE, CENTRE_PULL, CLUSTER_RADIUS, CORNER_PULL, DEPTH_PULL, FRICTION, GROUP_GAP, GROUP_PUSH, LINK_LENGTH, LINK_OVERLAP, LINK_STIFFNESS, LOST_SPREAD, MAX_SPEED, MAX_WORKERS, PLAIN_LINK_LENGTH, SPACING_RANGE, WARM_BUDGET_MS, WARM_COOLING, WARM_STEPS, WORKER_FROM } from "./constants";

/** Running and resting the layout. */
export interface SceneLayout {
  /**
   * One step of the force simulation. Gives `false` when it could not take one yet (a big
   * graph waiting for its pushes from the worker): the layout's energy then stays as it is.
   */
  tick(): boolean;
  /**
   * Runs the simulation to rest straight away (used on first load). A big graph gets at most
   * {@link WARM_BUDGET_MS} of it, then keeps settling on screen (`alpha` stays above 0), so the
   * page never freezes.
   */
  warm(): void;
  /** Gives the simulation some energy so the layout adapts. */
  reheat(amount: number): void;
  /**
   * Says the nodes, links, groups or filters changed: what each step reads from them (which
   * links leave a group and how hard they pull, each node's corner) is worked out again before
   * the next step, instead of at every step.
   */
  relink(): void;
}

/**
 * Where a node in these categories gathers (at spacing 1). Each category of the graph (`order`)
 * has its corner on a circle, the first on the left, the others turning round from there; a
 * node in no category or in several gathers near the {@link CENTRE}. A lone category has its
 * corner in the middle.
 */
export function clusterOf(order: readonly string[], categories: readonly string[]): [number, number] {
  const only = categories.length === 1 ? categories[0] : undefined;
  if (only === undefined || order.length < 2) return CENTRE;
  const at = order.findIndex((c) => sameCategory(c, only));
  if (at < 0) return CENTRE;
  const angle = Math.PI + (2 * Math.PI * at) / order.length;
  return [Math.cos(angle) * CLUSTER_RADIUS, Math.sin(angle) * CLUSTER_RADIUS];
}

/** `spacing` brought inside {@link SPACING_RANGE} (1 when it is not a number). */
export function clampSpacing(spacing: number): number {
  return Number.isFinite(spacing) ? Math.min(Math.max(spacing, SPACING_RANGE.min), SPACING_RANGE.max) : 1;
}

/** How links pull when groups are shown, and how many links leave each node's group. */
interface Crossing {
  /** The pull of each link leaving a group (links inside one group pull fully and are not listed). */
  pull: Map<SimLink, number>;
}

/**
 * The pull of every visible link that leaves a group. Such a link pulls less the more links
 * leave the group at either of its ends: a node's links out of its group share one link's
 * worth of pull. Otherwise a node with many links to other groups (common in scanned projects,
 * where most links cross packages) is torn out of its group, the group's circle swells to
 * reach it and covers its neighbours.
 */
function crossingLinks(links: SimLink[], parents: Map<string, string | null>): Crossing {
  const out = new Map<SimNode, number>();
  const leaving: [SimLink, number][] = [];
  for (const l of links) {
    if (!l.visible) continue;
    const pull = linkPull(l.a.node.group ?? null, l.b.node.group ?? null, parents);
    if (pull >= 1) continue;
    leaving.push([l, pull]);
    out.set(l.a, (out.get(l.a) ?? 0) + 1);
    out.set(l.b, (out.get(l.b) ?? 0) + 1);
  }
  const pull = new Map<SimLink, number>();
  for (const [l, p] of leaving) pull.set(l, p / Math.max(out.get(l.a)!, out.get(l.b)!));
  return { pull };
}

/** What a step reads from the graph and the filters, kept until they change (see {@link SceneLayout.relink}). */
interface Prepared {
  /** The visible nodes. */
  visible: SimNode[];
  /** The visible links that leave a group, with their pull (`null`: groups not shown). */
  crossing: Crossing | null;
  /** The corner each visible node gathers in (at spacing 1). */
  corners: Map<SimNode, [number, number]>;
  /** The visible links. */
  links: SimLink[];
  /** Each of {@link links}' length at rest at spacing 1, its ends' sizes aside. */
  rest: Float64Array;
  /** Each of {@link links}' pull (less than 1 for a link leaving a group, see {@link crossingLinks}). */
  pulls: Float64Array;
  /** The visible nodes outside any shown group (pushed apart from the groups). */
  loose: SimNode[];
}

/**
 * The workers computing pushes, made the first time a big graph needs them (`null`: none can
 * be, the push is then computed in the step). One per spare processor core, up to four: each
 * computes the push on its share of the nodes.
 */
let workers: Worker[] | null | undefined;

/** The workers, made on first use (`null` where workers are not available). */
function pushWorkers(): Worker[] | null {
  if (workers !== undefined) return workers;
  try {
    const count = Math.min(Math.max((navigator.hardwareConcurrency || 2) - 1, 1), MAX_WORKERS);
    workers = typeof Worker === "function" ? Array.from({ length: count }, () => new Worker(new URL("../repulsionWorker.ts", import.meta.url), { type: "module" })) : null;
  } catch {
    workers = null;
  }
  return workers;
}

/** The layout of `scene`. */
export function forces(scene: Scene): SceneLayout {
  let prepared: Prepared | null = null;
  /** What the steps read, worked out once after each change. */
  const prepare = (): Prepared => {
    if (prepared) return prepared;
    const visible = scene.nodes.filter((n) => n.visible);
    const grouped = scene.showGroups && scene.groups.length > 0;
    const parents = new Map(scene.groups.map((g) => [g.id, g.group.parent ?? null]));
    const corners = new Map(visible.map((n) => [n, clusterOf(scene.categories, n.node.categories)]));
    const crossing = grouped ? crossingLinks(scene.links, parents) : null;
    const links = scene.links.filter((l) => l.visible);
    const rest = Float64Array.from(links, (l) => LINK_LENGTH[l.link.kind] ?? PLAIN_LINK_LENGTH);
    const pulls = Float64Array.from(links, (l) => crossing?.pull.get(l) ?? 1);
    const loose = visible.filter((n) => !n.node.group || !scene.groupById.has(n.node.group));
    return (prepared = { visible, crossing, corners, links, rest, pulls, loose });
  };
  /** Numbers each request to the workers; an answer for nodes that changed since is dropped. */
  let ticket = 0;
  /** The request on its way, if one is: its ticket, the nodes it is for (in order), and how many shares are still to come. */
  let asked: { ticket: number; nodes: SimNode[]; waiting: number; found: Pushes } | null = null;
  /** The pushes back from the workers, ready for the next step. */
  let ready: Pushes | null = null;

  /** Sends where `nodes` are to the workers, each computing a share of them. */
  const ask = (pool: Worker[], nodes: SimNode[], deep: boolean) => {
    const n = nodes.length;
    const found: Pushes = { fx: new Float64Array(n), fy: new Float64Array(n), fz: new Float64Array(n) };
    asked = { ticket: ++ticket, nodes, waiting: pool.length, found };
    const share = Math.ceil(n / pool.length);
    // Read once; each worker gets its own copy, since sending it hands the buffers over.
    const read = placesOf(nodes, deep);
    pool.forEach((w, k) => {
      const places = k === pool.length - 1 ? read : { x: read.x.slice(), y: read.y.slice(), z: read.z.slice(), r: read.r.slice() };
      const request: PushRequest = { ticket, deep, spacing: scene.spacing, from: Math.min(k * share, n), to: Math.min((k + 1) * share, n), ...places };
      w.postMessage(request, [places.x.buffer, places.y.buffer, places.z.buffer, places.r.buffer]);
    });
  };
  /** Takes a worker's share, when it is for the nodes asked about; once all are in, the pushes are ready. */
  const answered = (e: MessageEvent<PushAnswer>) => {
    const a = e.data;
    if (!asked || a.ticket !== asked.ticket) return;
    asked.found.fx.set(a.fx, a.from);
    asked.found.fy.set(a.fy, a.from);
    asked.found.fz.set(a.fz, a.from);
    if (--asked.waiting === 0) {
      ready = asked.found;
      scene.redraw = true;
    }
  };

  /**
   * Adds the push between `visible` nodes to their speeds. Gives `false` when a big graph's
   * pushes are not back from the workers yet (the step must wait).
   */
  const push = (visible: SimNode[], alpha: number, deep: boolean): boolean => {
    const pool = visible.length >= WORKER_FROM ? pushWorkers() : null;
    if (!pool) {
      repel(visible, alpha, deep, scene.spacing);
      return true;
    }
    for (const w of pool) if (!w.onmessage) w.onmessage = answered as (e: MessageEvent) => void;
    const forNodes = asked?.nodes;
    if (ready && forNodes === visible) {
      applyPushes(visible, ready, alpha);
      ready = null;
      ask(pool, visible, deep);
      return true;
    }
    // Nothing asked yet, or asked about other nodes (the graph or the filters changed): ask now.
    if (forNodes !== visible) {
      ready = null;
      ask(pool, visible, deep);
    }
    return false;
  };
  return {
    relink() {
      prepared = null;
    },

    tick() {
      const { visible, crossing, corners, links, rest, pulls, loose } = prepare();
      const a = scene.alpha;
      const deep = scene.dimension === "3d";
      const grouped = !!crossing;
      const spacing = scene.spacing;
      if (!push(visible, a, deep)) return false;
      for (let i = 0; i < links.length; i++) {
        const l = links[i]!;
        const dx = l.b.x - l.a.x;
        const dy = l.b.y - l.a.y;
        const dz = deep ? l.b.z - l.a.z : 0;
        const d = Math.sqrt(dx * dx + dy * dy + dz * dz) || 0.01;
        const f = (d - (rest[i]! * spacing + l.a.r + l.b.r - LINK_OVERLAP)) * LINK_STIFFNESS * pulls[i]! * a;
        const fx = (dx / d) * f;
        const fy = (dy / d) * f;
        const fz = (dz / d) * f;
        l.a.vx += fx;
        l.a.vy += fy;
        l.a.vz += fz;
        l.b.vx -= fx;
        l.b.vy -= fy;
        l.b.vz -= fz;
      }
      // A big graph's groups keep the circles of the end of the step before: worked out again here,
      // they would barely differ, at the cost of a pass over every node.
      if (grouped) {
        if (visible.length < WORKER_FROM) scene.layoutGroups();
        pullTogether(scene.groups, a);
      }
      for (const n of visible) {
        const c = corners.get(n) ?? CENTRE;
        const pull = c === CENTRE ? CENTRE_PULL : CORNER_PULL;
        const gx = (c[0] * spacing - n.x) * pull;
        const gy = (c[1] * spacing - n.y) * pull;
        n.vx += gx * a;
        n.vy += gy * a;
        // In depth, everything is gently held around the middle, like in the plane.
        n.vz = deep ? n.vz - n.z * DEPTH_PULL * a : 0;
        if (n.fixed) continue;
        n.vx *= FRICTION;
        n.vy *= FRICTION;
        n.vz *= FRICTION;
        const speed = Math.sqrt(n.vx * n.vx + n.vy * n.vy + n.vz * n.vz);
        if (speed > MAX_SPEED) {
          n.vx *= MAX_SPEED / speed;
          n.vy *= MAX_SPEED / speed;
          n.vz *= MAX_SPEED / speed;
        }
        n.x += n.vx;
        n.y += n.vy;
        n.z += n.vz;
        // A broken place (a push or a position that was not a number) would stay broken for
        // good, and spread to its links and group: the node starts again in its corner.
        if (!Number.isFinite(n.x + n.y + n.z)) Object.assign(n, { x: c[0] * spacing + Math.random() * LOST_SPREAD, y: c[1] * spacing + Math.random() * LOST_SPREAD, z: 0, vx: 0, vy: 0, vz: 0 });
      }
      if (grouped) {
        scene.layoutGroups();
        pushApart(scene.groups, loose, GROUP_GAP * spacing, GROUP_PUSH);
      }
      return true;
    },

    warm() {
      scene.alpha = 1;
      const until = performance.now() + WARM_BUDGET_MS;
      let done = true;
      for (let i = 0; i < WARM_STEPS; i++) {
        // A big graph's steps wait for the worker: it settles on screen instead.
        if (!scene.tick()) {
          done = false;
          break;
        }
        scene.alpha *= WARM_COOLING;
        if (performance.now() > until) {
          done = false;
          break;
        }
      }
      if (done) scene.alpha = 0;
      scene.fit();
    },

    reheat(amount) {
      scene.alpha = Math.max(scene.alpha, amount);
    },
  };
}

/** `true` if the browser asked for reduced motion: the layout then settles instantly. */
export function prefersReducedMotion(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}
